import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Users, 
  Target, 
  Award, 
  Globe,
  ArrowRight,
  CheckCircle,
  Star,
  Zap,
  Shield,
  Lightbulb,
  Heart,
  Coffee
} from 'lucide-react'

const About: React.FC = () => {
  const milestones = [
    {
      year: '2023',
      title: '公司成立',
      description: 'DeepLink Limited正式成立，专注于Web3硬件设备研发'
    },
    {
      year: '2023 Q1',
      title: '种子轮融资',
      description: '完成种子轮融资，获得$2M资金支持'
    },
    {
      year: '2023 Q3',
      title: 'A轮融资',
      description: '完成A轮融资，获得$10M资金支持'
    },
    {
      year: '2024 Q1',
      title: '产品发布',
      description: 'CoreTrust手机正式发布，开启Web3手机新时代'
    },
    {
      year: '2024 Q2',
      title: 'B轮融资',
      description: '正在进行B轮融资，目标$25M'
    }
  ]

  const values = [
    {
      icon: Shield,
      title: '安全第一',
      description: '我们始终将用户安全放在首位，采用最先进的安全技术'
    },
    {
      icon: Zap,
      title: '创新驱动',
      description: '持续创新，推动Web3技术的发展和应用'
    },
    {
      icon: Users,
      title: '用户至上',
      description: '以用户需求为导向，提供最佳的产品体验'
    },
    {
      icon: Globe,
      title: '开放合作',
      description: '与合作伙伴共同构建Web3生态系统'
    }
  ]

  const team = [
    {
      name: '张三',
      position: 'CEO & 创始人',
      description: '前华为技术专家，10年移动设备开发经验',
      avatar: '👨‍💼'
    },
    {
      name: '李四',
      position: 'CTO & 联合创始人',
      description: '区块链技术专家，前蚂蚁金服技术负责人',
      avatar: '👨‍💻'
    },
    {
      name: '王五',
      position: 'COO & 联合创始人',
      description: '产品运营专家，前腾讯产品总监',
      avatar: '👩‍💼'
    },
    {
      name: '赵六',
      position: 'CFO',
      description: '财务专家，前红杉资本投资经理',
      avatar: '👨‍💼'
    }
  ]

  return (
    <>
      <Helmet>
        <title>关于我们 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited公司介绍，专注于Web3深度集成的硬件设备制造，CoreTrust手机产品。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                关于我们
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                专注于Web3深度集成的硬件设备制造商
              </p>
            </motion.div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  我们的使命
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  DeepLink Limited致力于推动Web3技术的普及和应用，通过创新的硬件设备，
                  为用户提供安全、便捷的Web3体验。我们相信，Web3技术将改变人们的生活方式，
                  让数字世界更加开放、透明、去中心化。
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  我们的愿景是成为Web3硬件设备领域的领导者，构建完整的Web3生态系统，
                  让每个人都能轻松享受Web3技术带来的便利。
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/products"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <span>了解产品</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/careers"
                    className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300"
                  >
                    加入我们
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">核心价值</h3>
                  <div className="space-y-4">
                    {values.map((value, index) => {
                      const Icon = value.icon
                      return (
                        <div key={value.title} className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{value.title}</h4>
                            <p className="text-sm text-white/80">{value.description}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                核心团队
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们拥有一支经验丰富、充满激情的团队
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="text-6xl mb-4">{member.avatar}</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 dark:text-blue-400 font-semibold mb-3">
                    {member.position}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {member.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                发展历程
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                从成立到现在，我们一直在稳步前进
              </p>
            </motion.div>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-600 to-purple-600"></div>
              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.year}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  >
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                        <div className="text-blue-600 dark:text-blue-400 font-bold text-lg mb-2">
                          {milestone.year}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center relative z-10">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                    <div className="w-1/2"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                加入我们的旅程
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                与我们一起构建Web3的未来，创造改变世界的产品
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/careers"
                  className="bg-white text-indigo-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <span>查看职位</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300"
                >
                  联系我们
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default About